<template>
  <b-card
    class="exception exception--weekdays"
    :title="$t('components.loanable.exceptions.weekdays.type') | capitalize"
  >
    <div class="l-exception-frame">
      <div class="l-exception-frame__content">
        <div class="l-exception-frame__date">
          <b-form-group>
            <b-form-checkbox-group
              :id="`exception-${id}-weekdays`"
              :disabled="disabled"
              :checked="exception.scope"
              :name="`exception-${id}-weekdays`"
              class="exception__days"
              @change="emitChange('scope', $event)"
            >
              <b-form-checkbox value="SU">{{ $t("weekdays.sunday") }}</b-form-checkbox>
              <b-form-checkbox value="MO">{{ $t("weekdays.monday") }}</b-form-checkbox>
              <b-form-checkbox value="TU">{{ $t("weekdays.tuesday") }}</b-form-checkbox>
              <b-form-checkbox value="WE">{{ $t("weekdays.wednesday") }}</b-form-checkbox>
              <b-form-checkbox value="TH">{{ $t("weekdays.thursday") }}</b-form-checkbox>
              <b-form-checkbox value="FR">{{ $t("weekdays.friday") }}</b-form-checkbox>
              <b-form-checkbox value="SA">{{ $t("weekdays.saturday") }}</b-form-checkbox>
            </b-form-checkbox-group>
          </b-form-group>
        </div>

        <div class="l-exception-frame__time">
          <b-form-group
            :description="$t('components.loanable.exceptions.weekdays.time_description')"
          >
            <b-form-group
              :label="$t('components.loanable.exceptions.time_start')"
              :label-for="`exception-${id}-period-start`"
            >
              <duration-select
                :id="`exception-${id}-period-start`"
                :value="periodStart"
                :start="{ hours: 0, minutes: 0 }"
                :step="{ hours: 0, minutes: stepMinutes }"
                :end="periodStartEndTime"
                @change="setPeriodStart"
              >
              </duration-select>
            </b-form-group>

            <b-form-group
              :label="$t('components.loanable.exceptions.time_end')"
              :label-for="`exception-${id}-period-end`"
            >
              <duration-select
                :id="`exception-${id}-period-end`"
                :value="periodEnd"
                :start="periodEndStartTime"
                :step="{ hours: 0, minutes: stepMinutes }"
                :end="{ hours: 24, minutes: 0 }"
                @change="setPeriodEnd"
              >
              </duration-select>
            </b-form-group>
          </b-form-group>
        </div>
      </div>

      <div class="l-exception-frame__actions">
        <icon-button role="remove-item" @click.prevent="$emit('delete')">{{
          $t("forms.remove") | capitalize
        }}</icon-button>
      </div>
    </div>
  </b-card>
</template>

<script>
import DurationSelect from "@/components/Forms/DurationSelect.vue";
import IconButton from "@/components/shared/IconButton.vue";

export default {
  name: "ExceptionWeekdays",
  components: { DurationSelect, IconButton },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    /* Example:
         {
           "available": false,
           "type": "weekdays",
           "scope": [ "WE" ],
           "period": "0:0-24:0"
         }
    */
    exception: {
      type: Object,
      required: true,
    },
    id: {
      type: [String, Number],
      required: true,
    },
    stepMinutes: {
      type: Number,
      default: 15,
    },
  },
  computed: {
    periodDates() {
      if (!this.exception.period) {
        return [null, null];
      }

      return this.parsePeriodStr(this.exception.period);
    },
    periodStart() {
      if (!this.periodDates || !this.periodDates[0]) {
        return null;
      }

      return this.periodDates[0];
    },
    periodEnd() {
      if (!this.periodDates || !this.periodDates[1]) {
        return null;
      }

      return this.periodDates[1];
    },
    periodStartEndTime() {
      if (!this.periodDates || !this.periodDates[1]) {
        return { hours: 23, minutes: 60 - this.stepMinutes };
      }

      const endMinute = this.periodDates[1].hours * 60 + this.periodDates[1].minutes - 15;
      return { hours: Math.floor(endMinute / 60), minutes: endMinute % 60 };
    },
    periodEndStartTime() {
      if (!this.periodDates || !this.periodDates[1]) {
        return { hours: 0, minutes: 0 };
      }

      const startMinute = this.periodDates[0].hours * 60 + this.periodDates[0].minutes + 15;
      return { hours: Math.floor(startMinute / 60), minutes: startMinute % 60 };
    },
  },
  methods: {
    formatTimeStr(time) {
      // Format: HH:mm
      return String(time.hours).padStart(2, "0") + ":" + String(time.minutes).padStart(2, "0");
    },
    formatPeriodStr(period) {
      // Format: HH:mm-HH:mm
      return [this.formatTimeStr(period[0]), this.formatTimeStr(period[1])].join("-");
    },
    parsePeriodStr(periodStr) {
      let startTime, endTime;
      [startTime, endTime] = periodStr.split("-");

      let timeParts = startTime.split(":").map(Number);
      startTime = { hours: timeParts[0], minutes: timeParts[1] };
      timeParts = endTime.split(":").map(Number);
      endTime = { hours: timeParts[0], minutes: timeParts[1] };

      if (endTime.hours === 0 && endTime.minutes === 0) {
        endTime = { hours: 24, minutes: 0 };
      }

      return [startTime, endTime];
    },
    setPeriodStart(periodStart) {
      // Given period, or start of day.
      periodStart = periodStart ?? { hours: 0, minutes: 0 };

      // Snap to a multiple of stepMinutes
      let minuteStart = periodStart.hours * 60 + periodStart.minutes;
      minuteStart = this.stepMinutes * Math.round(minuteStart / this.stepMinutes);

      // Max start is the end of the day minus one step.
      const minMinuteStart = 0;
      const maxMinuteStart = 24 * 60 - this.stepMinutes;
      minuteStart = Math.max(minuteStart, minMinuteStart);
      minuteStart = Math.min(minuteStart, maxMinuteStart);

      periodStart = {
        hours: Math.floor(minuteStart / 60),
        minutes: minuteStart % 60,
      };

      // Ensure end >= start + stepMinutes
      let periodEnd = this.periodDates[1];
      if (periodEnd.hours * 60 + periodEnd.minutes <= minuteStart + this.stepMinutes) {
        const minuteEnd = minuteStart + this.stepMinutes;
        periodEnd = {
          hours: Math.floor(minuteEnd / 60),
          minutes: minuteEnd % 60,
        };
      }

      this.emitChange("period", this.formatPeriodStr([periodStart, periodEnd]));
    },
    setPeriodEnd(periodEnd) {
      periodEnd = periodEnd ?? { hours: 24, minutes: 0 };

      if (periodEnd.hours === 0 && periodEnd.minutes === 0) {
        periodEnd = { hours: 24, minutes: 0 };
      }

      // Snap to a multiple of stepMinutes
      let minuteEnd = periodEnd.hours * 60 + periodEnd.minutes;
      minuteEnd = this.stepMinutes * Math.round(minuteEnd / this.stepMinutes);

      // Min end is the start of the day plus one step.
      const minMinuteEnd = this.stepMinutes;
      const maxMinuteEnd = 24 * 60;
      minuteEnd = Math.max(minuteEnd, minMinuteEnd);
      minuteEnd = Math.min(minuteEnd, maxMinuteEnd);

      periodEnd = {
        hours: Math.floor(minuteEnd / 60),
        minutes: minuteEnd % 60,
      };

      // Ensure start <= end - stepMinutes
      let periodStart = this.periodDates[0];
      if (periodStart.hours * 60 + periodStart.minutes >= minuteEnd - this.stepMinutes) {
        const minuteStart = minuteEnd - this.stepMinutes;
        periodStart = {
          hours: Math.floor(minuteStart / 60),
          minutes: minuteStart % 60,
        };
      }

      this.emitChange("period", this.formatPeriodStr([periodStart, periodEnd]));
    },
    emitChange(key, value) {
      this.$emit("change", this.exception, key, value);
    },
    selectDates(dates) {
      this.emitChange("scope", dates);
    },
  },
};
</script>

<style lang="scss">
.exception--weekdays {
  .exception__days {
    display: flex;
    flex-direction: column;
  }
}
</style>
